import { ArrowRight, ChevronDown, Loader2, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ManagedFlowCard } from "./managed-flow-card";

interface ConnectGoogleBusinessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  onDirectConnect: () => void;
  initiating: boolean;

  managerEmail: string | null;
  onManagedFlowSubmitted: () => void;
  notifying: boolean;
}

/**
 * Connect dialog with a primary "Direct OAuth" CTA and a collapsed
 * secondary "Let 96digital manage" path. Replaces the previous two
 * side-by-side cards on the settings page — we surface the easy path
 * (OAuth) first and gate the manager-invite guide behind a disclosure so
 * the page no longer presents two equally weighted options.
 */
export function ConnectGoogleBusinessDialog({
  open,
  onOpenChange,
  onDirectConnect,
  initiating,
  managerEmail,
  onManagedFlowSubmitted,
  notifying,
}: ConnectGoogleBusinessDialogProps) {
  const { t } = useTranslation("settings");
  const [managedExpanded, setManagedExpanded] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("reviews.connect.dialog.title")}</DialogTitle>
          <DialogDescription>{t("reviews.connect.dialog.description")}</DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          {/* Primary path — full-width, larger, outlined to keep the
              dialog visually quiet on the dim modal overlay. Hierarchy is
              carried by size (lg, two-line) rather than fill weight. */}
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={onDirectConnect}
            disabled={initiating}
            className="w-full justify-between h-auto px-3 py-4 text-left"
          >
            <span className="flex flex-col gap-0.5">
              <span className="font-semibold">{t("reviews.connect.dialog.directTitle")}</span>
              <span className="text-xs font-normal text-muted-foreground">
                {t("reviews.connect.dialog.directDescription")}
              </span>
            </span>
            {initiating ? (
              <Loader2 className="w-5 h-5 animate-spin shrink-0" />
            ) : (
              <ArrowRight className="w-5 h-5 shrink-0" />
            )}
          </Button>

          {/* Secondary path — same outlined style at default size so it
              matches the primary visually but reads as shorter/less
              important. Expands the manager-invite guide inline; we don't
              auto-expand because most users should use OAuth and the
              disclosure keeps the default state clean. */}
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setManagedExpanded((prev) => !prev)}
              className="w-full justify-between px-3"
              aria-expanded={managedExpanded}
            >
              <span className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                {t("reviews.connect.dialog.managedToggle")}
              </span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${managedExpanded ? "rotate-180" : ""}`}
              />
            </Button>
            {managedExpanded && (
              <ManagedFlowCard
                managerEmail={managerEmail}
                onIveAdded={onManagedFlowSubmitted}
                isLoading={notifying}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
